import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetPassword } from "../../../redux/actions/user.actions";
import { AppDispatch } from "../../../redux/store";
import RootState from "../../../redux/states/root.state";
import AuthAlert from "./AuthAlert";
import PasswordInput from "./PasswordInput";

const ResetPasswordForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const resetError = useSelector((state: RootState) => state.user.error);
  const loading = useSelector((state: RootState) => state.user.loading);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // The reset email links here with ?token=&email=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const email = params.get("email");

    if (token) {
      setToken(token);
    }
    if (email) {
      setEmail(email);
    }
  }, [location]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(
      resetPassword({ email, password, password_confirmation: passwordConfirmation, token })
    );

    if (resetPassword.fulfilled.match(result)) {
      setSuccessMessage(result.payload.message || "Password reset successfully.");

      // Give the user a moment to read the confirmation, then send them to log in
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    }
  };

  const done = successMessage !== "";

  return (
    <form className="auth-form" onSubmit={handleResetPassword} noValidate>
      <div className="field">
        <label className="field-label" htmlFor="email">Email address</label>
        <input
          id="email"
          name="email"
          className="input"
          type="email"
          autoComplete="email"
          autoFocus={!email}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={done}
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="password">New password</label>
        <PasswordInput
          id="password"
          name="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          disabled={done}
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="password_confirmation">Confirm new password</label>
        <PasswordInput
          id="password_confirmation"
          name="password_confirmation"
          value={passwordConfirmation}
          onChange={setPasswordConfirmation}
          autoComplete="new-password"
          required
          disabled={done}
        />
      </div>

      {resetError && <AuthAlert kind="error" message={resetError} />}
      {done && <AuthAlert kind="success" message={`${successMessage} Taking you to log in…`} />}

      <div className="auth-actions">
        <button type="submit" className="btn btn-primary" disabled={loading || done}>
          {loading ? <span className="spinner auth-btn-spinner" aria-label="Resetting password" /> : "Reset password"}
        </button>
      </div>

      <p className="auth-footer">
        <Link to="/login" className="auth-link">Back to log in</Link>
      </p>
    </form>
  );
};

export default ResetPasswordForm;
